import { useEffect, useState } from "react";
import { guardarConsentimiento, leerConsentimiento, PIXEL_ID } from "@/lib/captacion";
import { cn } from "@/lib/utils";
import { Boton } from "./Boton";

// Lo que flota abajo de la pantalla: el banner de consentimiento y, en mobile, la barra con el CTA.
// Viven juntos porque compiten por el mismo borde: mientras el banner espera respuesta, la barra
// no aparece (dos franjas apiladas taparían un tercio de un teléfono).

const esCampo = (el) => el && ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName);

export const BarrasFlotantes = () => {
  // El banner sólo existe si hay algo que consentir: sin Pixel configurado no se mide nada.
  const [pideConsentimiento, setPideConsentimiento] = useState(
    () => Boolean(PIXEL_ID) && leerConsentimiento() === null,
  );
  const [heroVisible, setHeroVisible] = useState(true);
  const [demoVisible, setDemoVisible] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);

  // "¿Sigue a la vista el hero?" no se puede preguntar al hero: está FIJO (`LandingPage`), así que
  // nunca sale de la pantalla — queda tapado. Se mide la capa que lo cubre: cuando "Cómo funciona"
  // pasó la mitad de la pantalla, el hero ya quedó atrás y la barra puede aparecer.
  useEffect(() => {
    const cubierta = document.getElementById("como-funciona");
    const onScroll = () => {
      if (cubierta) setHeroVisible(cubierta.getBoundingClientRect().top > window.innerHeight * 0.5);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const demo = document.getElementById("demo");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => setDemoVisible(e.isIntersecting));
    });
    if (demo) obs.observe(demo);
    return () => obs.disconnect();
  }, []);

  // Con el teclado abierto la barra quedaría encima del campo que se está llenando.
  useEffect(() => {
    const onIn = (e) => setEscribiendo(esCampo(e.target));
    const onOut = () => setEscribiendo(false);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  const responder = (valor) => {
    guardarConsentimiento(valor);
    setPideConsentimiento(false);
  };

  const barraVisible = !pideConsentimiento && !heroVisible && !demoVisible && !escribiendo;

  return (
    <>
      {pideConsentimiento && (
        <div
          className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-lg rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-900/10"
          role="dialog"
          aria-label="Preferencias de medición"
          data-testid="banner-consentimiento"
        >
          <p className="text-sm leading-relaxed text-slate-600">
            Usamos cookies de medición para saber qué campañas funcionan. Puedes aceptarlas o rechazarlas; la página
            funciona igual. Más detalle en la{" "}
            <a href="/privacidad" className="font-semibold text-slate-800 underline decoration-slate-300 underline-offset-2">política de privacidad</a>.
          </p>
          <div className="mt-4 flex gap-2">
            <Boton as="button" type="button" onClick={() => responder("aceptado")} data-testid="consentimiento-aceptar">
              Aceptar
            </Boton>
            <Boton as="button" type="button" variante="secundario" onClick={() => responder("rechazado")} data-testid="consentimiento-rechazar">
              Rechazar
            </Boton>
          </div>
        </div>
      )}

      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md transition-transform duration-300 md:hidden",
          barraVisible ? "translate-y-0" : "translate-y-full",
        )}
        aria-hidden={!barraVisible}
        data-testid="barra-cta-movil"
      >
        <Boton href="#demo" tamano="lg" className="w-full" tabIndex={barraVisible ? 0 : -1}>
          Agenda una demo
        </Boton>
      </div>
    </>
  );
};
